/*******************************************************************************
 * Copyright (c) 2024-2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {PspError} from '@shared/models/psp-error';
import {
  NotificationData,
  NotificationType,
} from '@shared/common-core/notification/models/notification-data';
import {APP_CONFIG} from '@common-core/config/app-config';
import {EnvironmentConfig} from '@shared/models/environment-config';

export const SUPPRESSED_ERRORS = ['Missing, invalid or expired access token'];

@Injectable({
  providedIn: 'root',
})
export class PspNotificationHelper {
  private translateService = inject(TranslateService);
  private appConfig = inject<EnvironmentConfig>(APP_CONFIG);

  /**
   * This function is used to return the alias of a given error
   * @param error the error to be checked
   * @returns the alias of the notification
   */
  public getErrorMessageNotification(error: PspError): NotificationData | null {
    // If the error should be suppressed, return null
    if (SUPPRESSED_ERRORS.includes(error.message)) {
      return null;
    }

    // Help users to check their VPN connection on Develop and UAT environments
    if (
      error.message?.includes('An error occurred with code: 0') &&
      (this.appConfig.cloudEnvironment.includes('develop') ||
        this.appConfig.cloudEnvironment.includes('uat'))
    ) {
      return {
        message: this.translateService.instant('system.notifications.messages.vpn_error'),
        type: NotificationType.ERROR,
        title: this.translateService.instant('system.notifications.titles.error'),
      };
    }

    // If the error has a message, return it
    if (error.message) {
      return {
        message: error.statusReason
          ? error.statusReasonCode + ' - ' + error.statusReason
          : error.message,
        type: NotificationType.ERROR,
        title: this.translateService.instant('system.notifications.titles.error'),
      };
    }

    // If the error has no message, return the translated message of the error code
    let notificationAlias = null;

    // If the error message has no alias and is a Bad Request, return bad_request alias
    if (!notificationAlias && error.httpCode === 400) {
      notificationAlias = 'system.notifications.messages.bad_request';
    }
    // If the error message has no alias and is a Server Error, return server_error alias
    if (!notificationAlias && error.httpCode === 500) {
      notificationAlias = 'system.notifications.messages.server_error';
    }
    // If the error message has no alias, and it was not caught, return unknown_error alias
    if (!notificationAlias) {
      notificationAlias = 'system.notifications.messages.unknown_error';
    }

    return {
      message: this.translateService.instant(notificationAlias),
      type: NotificationType.ERROR,
      title: this.translateService.instant('system.notifications.titles.error'),
    };
  }

  /**
   * This function is used to return the alias of a given error
   * @param successMessageType message type to show
   * @returns the alias of the notification
   */
  public getSuccessMessageNotification(successMessageType: string): NotificationData {
    let notificationAlias: {title: string; message?: string} | null = null;

    if (successMessageType === 'emailCodeResent') {
      notificationAlias = {
        title: 'mfa.code_resent',
        message: 'mfa.code_resent_email',
      };
    }

    if (successMessageType === 'smsCodeResent') {
      notificationAlias = {
        title: 'mfa.code_resent',
        message: 'mfa.code_resent_device',
      };
    }

    if (successMessageType === 'mfaMethodUpdated') {
      notificationAlias = {
        title: 'dashboard.settings.security.notifications.method_updated_title',
        message: 'dashboard.settings.security.notifications.method_updated_message',
      };
    }

    if (successMessageType === 'resetPasswordEmailSent') {
      notificationAlias = {
        title: 'reset_password.title_notification_success',
        message: 'reset_password.title_message_success',
      };
    }

    if (successMessageType === 'apiKeyCreated') {
      notificationAlias = {
        title: 'dashboard.platform.api_keys.notifications.key_created_title',
        message: 'dashboard.platform.api_keys.notifications.key_created_message',
      };
    }

    if (successMessageType === 'apiKeyRevoked') {
      notificationAlias = {
        title: 'dashboard.platform.api_keys.notifications.key_revoked_title',
        message: 'dashboard.platform.api_keys.notifications.key_revoked_message',
      };
    }

    if (successMessageType === 'clientUpdated') {
      notificationAlias = {
        title: 'dashboard.products.clients.details.notifications.client_updated',
      };
    }

    if (successMessageType === 'deleteTeamUserSuccess') {
      notificationAlias = {
        title: 'dashboard.settings.team.users.notifications.user_deleted_title',
        message: 'dashboard.settings.team.users.notifications.user_deleted_message',
      };
    }

    if (successMessageType === 'updateTeamUserSuccess') {
      notificationAlias = {
        title: 'dashboard.settings.team.users.notifications.user_updated_title',
        message: 'dashboard.settings.team.users.notifications.user_updated_message',
      };
    }

    if (successMessageType === 'inviteTeamUserSuccess') {
      notificationAlias = {
        title: 'dashboard.settings.team.users.notifications.invite_team_user_title',
        message: 'dashboard.settings.team.users.notifications.invite_team_user_message',
      };
    }

    if (successMessageType === 'rolesChanged') {
      notificationAlias = {
        title: 'dashboard.settings.team.users.notifications.roles_changed_title',
        message: 'dashboard.settings.team.users.notifications.roles_changed_message',
      };
    }

    if (successMessageType === 'clientCreated') {
      notificationAlias = {
        title: 'dashboard.products.clients.details.notifications.client_created',
      };
    }

    if (successMessageType === 'transactionCreated') {
      notificationAlias = {
        title: 'dashboard.products.transactions.notifications.transaction_created',
      };
    }

    if (successMessageType === 'financialEntitiesImported') {
      notificationAlias = {
        title:
          'dashboard.products.financial_entities.notifications.financial_entities_imported',
      };
    }

    if (successMessageType === 'whitelistDomainAdded') {
      notificationAlias = {
        title:
          'dashboard.platform.whitelist_domain.notifications.domain_added_whitelist_title',
        message:
          'dashboard.platform.whitelist_domain.notifications.domain_added_whitelist_message',
      };
    }

    if (successMessageType === 'whitelistDomainDeleted') {
      notificationAlias = {
        title:
          'dashboard.platform.whitelist_domain.notifications.domain_deleted_whitelist_title',
        message:
          'dashboard.platform.whitelist_domain.notifications.domain_deleted_whitelist_message',
      };
    }

    if (successMessageType === 'blacklistDomainProcessed') {
      notificationAlias = {
        title:
          'dashboard.platform.blacklist_domain.notifications.domain_processed_blacklist_title',
        message:
          'dashboard.platform.blacklist_domain.notifications.domain_processed_blacklist_message',
      };
    }

    if (successMessageType === 'blacklistDomainDeleted') {
      notificationAlias = {
        title:
          'dashboard.platform.blacklist_domain.notifications.domain_deleted_blacklist_title',
        message:
          'dashboard.platform.blacklist_domain.notifications.domain_deleted_blacklist_message',
      };
    }

    if (successMessageType === 'sftpConfigurationUpdated') {
      notificationAlias = {
        title: 'dashboard.platform.sftp_keys.notifications.sftp_configuration_updated_title',
      };
    }

    if (successMessageType === 'sftpConfigurationDeleted') {
      notificationAlias = {
        title:
          'dashboard.platform.sftp_keys.notifications.sftp_configuration_keys_deleted_title',
      };
    }

    if (successMessageType === 'fileMapperUpdated') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_mappers.notifications.file_mapper_updated_title',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedpreSeedClients') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.pre_seed_client_updated_title',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedstrict') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.street_updated_title',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedencryppReturnFiles') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.encrypted_return_files_updated_title',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedoutputSummaryReportFile') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.output_summary_report_file',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedoutputNonProcessedTransactionsFile') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.output_non_processed_transactionsFile',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedoutputQualifyingClientsFile') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.output_qualifying_clients_file',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedoutputMonitoredClientsFile') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.output_monitored_clients_file',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedgenerateResponseReports') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.generate_response_reports',
      };
    }

    if (successMessageType === 'gmfConfigurationUpdatedsendInviteClientEmails') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.file_settings.notifications.send_invite_client_emails',
      };
    }

    if (successMessageType === 'gmfAdminConfigurationUpdatedautoActivateFinancialEntities') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.admin_settings.notifications.auto_activate_financial_entities_updated_title',
      };
    }

    if (successMessageType === 'integratorConnected') {
      notificationAlias = {
        title:
          'dashboard.products.financial_entities.integrator.notifications.integrator_connected_title',
      };
    }

    if (successMessageType === 'integratorDisconnected') {
      notificationAlias = {
        title:
          'dashboard.products.financial_entities.integrator.notifications.integrator_disconnected_title',
      };
    }

    if (successMessageType === 'spiKeyCreated') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_created_title',
      };
    }

    if (successMessageType === 'spiKeyUpdated') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_updated_title',
      };
    }

    if (successMessageType === 'spiKeyDeleted') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_deleted_title',
      };
    }

    if (successMessageType === 'clientAccountAdded') {
      notificationAlias = {
        title: 'dashboard.products.clients.details.notifications.client_account_added',
      };
    }

    if (successMessageType === 'iamAccountUpdated') {
      notificationAlias = {
        title: 'dashboard.platform.iam_accounts.notifications.account_updated_successfully',
      };
    }

    if (successMessageType === 'iamAccountDeleted') {
      notificationAlias = {
        title: 'dashboard.platform.iam_accounts.notifications.account_deleted_successfully',
      };
    }

    if (successMessageType === 'iamUserDeleted') {
      notificationAlias = {
        title: 'dashboard.platform.iam_users.notifications.user_deleted_successfully',
      };
    }

    if (successMessageType === 'iamUserUpdated') {
      notificationAlias = {
        title: 'dashboard.platform.iam_users.notifications.user_updated_successfully',
      };
    }

    if (successMessageType === 'iamUserPasswordReset') {
      notificationAlias = {
        title: 'dashboard.platform.iam_users.notifications.user_password_reset_successfully',
      };
    }

    if (successMessageType === 'iamUserPasswordSet') {
      notificationAlias = {
        title: 'dashboard.platform.iam_users.notifications.user_password_set_successfully',
      };
    }

    if (successMessageType === 'clientInvitationSent') {
      notificationAlias = {
        title: 'dashboard.products.clients.details.notifications.client_invitation_sent',
      };
    }

    if (successMessageType === 'businessLinkedSuccess') {
      notificationAlias = {
        title: 'dashboard.products.clients.details.notifications.business_linked_success',
      };
    }

    if (successMessageType === 'financialEntityDataReseted') {
      notificationAlias = {
        title:
          'dashboard.products.financial_entities.reset_data.notifications.financial_entity_data_reseted',
      };
    }

    if (successMessageType === 'financialEntityDeleted') {
      notificationAlias = {
        title:
          'dashboard.products.financial_entities.delete.notifications.financial_entity_data_deleted',
      };
    }

    if (successMessageType === 'webhookAdded') {
      notificationAlias = {
        title: 'dashboard.platform.webhooks.notifications.webhook_added_title',
        message: 'dashboard.platform.webhooks.notifications.webhook_added_message',
      };
    }

    if (successMessageType === 'webhookDeleted') {
      notificationAlias = {
        title: 'dashboard.platform.webhooks.notifications.webhook_deleted_title',
        message: 'dashboard.platform.webhooks.notifications.webhook_deleted_message',
      };
    }

    if (successMessageType === 'webhookUpdated') {
      notificationAlias = {
        title: 'dashboard.platform.webhooks.notifications.webhook_updated_title',
        message: 'dashboard.platform.webhooks.notifications.webhook_updated_message',
      };
    }

    if (successMessageType === 'webhookConfigurationApplied') {
      notificationAlias = {
        title: 'dashboard.platform.webhooks.notifications.webhook_configuration_applied_title',
        message:
          'dashboard.platform.webhooks.notifications.webhook_configuration_applied_message',
      };
    }

    if (successMessageType === 'participantDataResetSuccess') {
      notificationAlias = {
        title:
          'dashboard.products.participants.reset_data.notifications.participant_data_reset',
      };
    }

    if (successMessageType === 'entityDataResetSuccess') {
      notificationAlias = {
        title: 'dashboard.products.entities.reset_data.notifications.entity_data_reseted',
      };
    }

    if (successMessageType === 'participantDataDeletedSuccess') {
      notificationAlias = {
        title: 'dashboard.products.participants.delete.notifications.participant_data_deleted',
      };
    }

    if (successMessageType === 'participantUpdatedSuccess') {
      notificationAlias = {
        title:
          'dashboard.products.participants.participant_details.notifications.participant_details_updated',
      };
    }

    if (successMessageType === 'participantPaymentConfigurationUpdated') {
      notificationAlias = {
        title:
          'dashboard.products.participants.participant_payment_configuration.notifications.participant_payment_configuration_updated',
      };
    }

    if (successMessageType === 'participantConfigUpdatedSuccess') {
      notificationAlias = {
        title:
          'dashboard.products.participants.configuration.notifications.participant_environment_configured',
      };
    }

    if (successMessageType === 'resetParticipantKeysSuccess') {
      notificationAlias = {
        title:
          'dashboard.products.participants.configuration.notifications.reset_participant_keys_success',
      };
    }

    if (successMessageType === 'participantsCreated') {
      notificationAlias = {
        title:
          'dashboard.products.participants.configuration.notifications.participant_created',
      };
    }

    if (successMessageType === 'teamSizeUpdated') {
      notificationAlias = {
        title: 'dashboard.products.participants.team_size.notifications.team_size_updated',
      };
    }

    if (successMessageType === 'spiConfigurationUpdatedSuccess') {
      notificationAlias = {
        title:
          'dashboard.platform.configuration.spi_configuration.notifications.spi_configuration_updated',
      };
    }

    if (successMessageType === 'spiBillingRecalculationRequested') {
      notificationAlias = {
        title:
          'dashboard.platform.billing.notifications.spi_billing_recalculation_requested_title',
        message:
          'dashboard.platform.billing.notifications.spi_billing_recalculation_requested_message',
      };
    }

    if (successMessageType === 'entityConfigurationUpdated') {
      notificationAlias = {
        title: 'dashboard.products.entities.notifications.entity_configuration_updated_title',
        message: 'dashboard.products.entities.notifications.entity_configuration_updated_info',
      };
    }

    if (successMessageType === 'entityConfigurationCredentialsUpdated') {
      notificationAlias = {
        title:
          'dashboard.products.entities.notifications.entity_configuration_credentials_updated_title',
        message:
          'dashboard.products.entities.notifications.entity_configuration_credentials_updated_info',
      };
    }

    if (successMessageType === 'createBrebPaymentSuccess') {
      notificationAlias = {
        title: 'dashboard.products.payments.notifications.create_breb_payment_success_title',
      };
    }

    if (successMessageType === 'releasePaymentFundsSuccess') {
      notificationAlias = {
        title: 'dashboard.products.payments.notifications.release_payment_funds_success_title',
      };
    }

    if (successMessageType === 'rejectProcessingPaymentSuccess') {
      notificationAlias = {
        title:
          'dashboard.products.payments.notifications.reject_processing_payment_success_title',
      };
    }

    if (successMessageType === 'entityConfigurationUpdated') {
      notificationAlias = {
        title: 'dashboard.products.entities.notifications.entity_configuration_updated_title',
        message: 'dashboard.products.entities.notifications.entity_configuration_updated_info',
      };
    }

    if (successMessageType === 'accountLinkedSuccess') {
      notificationAlias = {
        title: 'dashboard.products.accounts.details.notifications.account_linked_success',
      };
    }

    if (successMessageType === 'feeConfigProposalSubmitted') {
      notificationAlias = {
        title:
          'dashboard.products.entities.fee_configuration.notifications.proposal_submitted_title',
      };
    }

    if (successMessageType === 'feeConfigProposalApproved') {
      notificationAlias = {
        title:
          'dashboard.products.entities.fee_configuration.notifications.proposal_approved_title',
      };
    }

    if (successMessageType === 'feeConfigProposalRejected') {
      notificationAlias = {
        title:
          'dashboard.products.entities.fee_configuration.notifications.proposal_rejected_title',
      };
    }

    if (successMessageType === 'keyOperationsExportDownloaded') {
      notificationAlias = {
        title:
          'dashboard.activity.auditing_logs.key_operations.notifications.export_download_success_title',
      };
    }

    if (successMessageType === 'auditingEventsExportDownloaded') {
      notificationAlias = {
        title:
          'dashboard.activity.auditing_logs.auditing_events.notifications.export_download_success_title',
      };
    }

    if (successMessageType === 'paymentOperationsExportDownloaded') {
      notificationAlias = {
        title:
          'dashboard.activity.auditing_logs.payment_operations.notifications.export_download_success_title',
      };
    }

    return {
      type: NotificationType.SUCCESS,
      title: this.translateService.instant(notificationAlias!.title),
      ...(notificationAlias!.message && {
        message: this.translateService.instant(notificationAlias!.message),
      }),
    };
  }

  /**
   * This function is used to return the alias of a given info
   * @param infoMessageType message type to show
   * @returns the alias of the notification
   */
  public getInfoMessageNotification(infoMessageType: string): NotificationData {
    let notificationAlias: {title: string; message?: string} | null = null;

    if (infoMessageType === 'spiKeyCreated') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_created_title',
        message: 'dashboard.products.spi_keys.notifications.spi_key_created_info',
      };
    }

    if (infoMessageType === 'spiKeyDeleted') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_deleted_title',
        message: 'dashboard.products.spi_keys.notifications.spi_key_deleted_info',
      };
    }

    if (infoMessageType === 'spiKeyUpdated') {
      notificationAlias = {
        title: 'dashboard.products.spi_keys.notifications.spi_key_updated_title',
        message: 'dashboard.products.spi_keys.notifications.spi_key_updated_info',
      };
    }

    return {
      type: NotificationType.INFO,
      title: this.translateService.instant(notificationAlias!.title),
      ...(notificationAlias!.message && {
        message: this.translateService.instant(notificationAlias!.message),
      }),
    };
  }
}
